// rebuildFilesIndex.ts
import {
  FirebaseStorage,
  ref,
  listAll,
  getMetadata,
  uploadString,
  StorageReference,
} from "firebase/storage";

/**
 * Matches what you currently store in *_files.json
 * (observed in -FGjn4l6eMlbUjsStqqH7_files.json) :contentReference[oaicite:2]{index=2}
 */
export type ProjectFileMetaData = {
  relativePath: string;   // storage fullPath
  size: number;           // KB (bytes / 1024)
  modifiedDate: string;   // ISO string
  fileNumber: number;     // deterministic-ish numeric id
};

export type ProjectFileMetaDataEntry = {
  "bazaart.ProjectFileMetaData": ProjectFileMetaData;
};

type LayersConfig = unknown;

/**
 * Public API:
 * - Reads layers_config.json to get layer IDs
 * - Lists files in:
 *    - <projectId>/ (root, excluding Layers recursion by default)
 *    - <projectId>/Layers/<layerId>/... (recursive)
 * - Writes <projectId>/<projectId>_files.json
 */
export async function rebuildProjectFilesIndex(params: {
  storage: FirebaseStorage;
  projectId: string;

  /**
   * Folder name that contains per-layer subfolders.
   * In your sample index it's "Layers" (capital L). :contentReference[oaicite:3]{index=3}
   */
  layersDirName?: string; // default "Layers"

  /**
   * If true, recursively lists EVERYTHING under <projectId>/ including Layers.
   * If false (default), it lists root files + per-layer folders only.
   */
  crawlEntireProjectTree?: boolean;

  /**
   * Path to layers config (default "<projectId>/layers_config.json").
   * Your sample index includes "<projectId>/layers_config.json". :contentReference[oaicite:4]{index=4}
   */
  layersConfigPath?: string;

  /**
   * Output index path (default "<projectId>/<projectId>_files.json").
   */
  outputIndexPath?: string;

  /**
   * Optional: concurrency for metadata fetching (keep modest; each file does getMetadata()).
   */
  metadataConcurrency?: number;

  /**
   * When set, use this as the project root path in storage (sync path).
   * Enables project-relative relativePath and sync-compatible output.
   */
  projectRootPath?: string;

  /**
   * When false, store size in bytes (for sync); when true (default), store size in KB.
   */
  sizeInKb?: boolean;

  /**
   * When false, build and return the index but do not upload _files.json (e.g. for sync recovery).
   * Default true.
   */
  uploadIndex?: boolean;
}): Promise<ProjectFileMetaDataEntry[]> {
  const projectRoot = params.projectRootPath ?? params.projectId;
  const {
    storage,
    projectId,
    layersDirName = "Layers",
    crawlEntireProjectTree = false,
    layersConfigPath = `${projectRoot}/layers_config.json`,
    outputIndexPath = `${projectRoot}/${params.projectId}_files.json`,
    metadataConcurrency = 8,
    sizeInKb = true,
    uploadIndex = true,
  } = params;

  // 1) Get layer IDs from layers_config.json (assumed to exist)
  const layerIds = await tryReadLayerIdsFromLayersConfig(storage, layersConfigPath);

  // 2) Collect file refs to index
  let fileRefs: StorageReference[] = [];

  if (crawlEntireProjectTree) {
    // Recursive crawl from project root (includes Layers, screenshots, configs, etc.)
    const rootRef = ref(storage, projectRoot);
    fileRefs = await listFilesRecursive(rootRef);
  } else {
    // a) root-level files (only direct children)
    const rootRef = ref(storage, projectRoot);
    const rootList = await listAll(rootRef);
    fileRefs.push(...rootList.items);

    // b) each layer folder recursively: <projectRoot>/Layers/<layerId>/...
    for (const layerId of layerIds) {
      const layerRef = ref(storage, `${projectRoot}/${layersDirName}/${layerId}`);
      const layerItems = await listFilesRecursive(layerRef);
      fileRefs.push(...layerItems);
    }
  }

  // Remove duplicates (can happen if caller sets crawlEntireProjectTree + layer listing, etc.)
  fileRefs = dedupeByFullPath(fileRefs);

  // 3) Fetch metadata and build entries
  const entries = await mapWithConcurrency(
    fileRefs,
    metadataConcurrency,
    async (fileRef): Promise<ProjectFileMetaDataEntry | null> => {
      try {
        const md = await getMetadata(fileRef);

        // Firebase Storage metadata: md.size is bytes, md.updated is ISO-ish string
        const sizeValue = sizeInKb ? md.size / 1024 : md.size;

        const modifiedDate =
          typeof md.updated === "string" && md.updated.length > 0
            ? new Date(md.updated).toISOString()
            : new Date().toISOString();

        // When using projectRootPath (sync), relativePath must be projects-relative (projectId/...)
        // so CloudUrl.create(path, deviceId) resolves to .../projects/projectId/...
        const relativePath =
          params.projectRootPath != null && fileRef.fullPath.startsWith(projectRoot + "/")
            ? `${projectId}/${fileRef.fullPath.slice(projectRoot.length + 1)}`
            : fileRef.fullPath;

        const fileNumber = stableNumericId(relativePath);

        return {
          "bazaart.ProjectFileMetaData": {
            relativePath,
            size: sizeValue,
            modifiedDate,
            fileNumber,
          },
        };
      } catch {
        // If something disappears mid-process, skip it (race with deletes).
        return null;
      }
    }
  );

  const filtered = entries.filter((e): e is ProjectFileMetaDataEntry => e !== null);

  // 4) Sort for consistency (optional but makes diffs stable)
  filtered.sort((a, b) =>
    a["bazaart.ProjectFileMetaData"].relativePath.localeCompare(
      b["bazaart.ProjectFileMetaData"].relativePath
    )
  );

  // 5) Upload as JSON (skip when uploadIndex is false, e.g. sync recovery)
  if (uploadIndex) {
    await uploadString(ref(storage, outputIndexPath), JSON.stringify(filtered), "raw", {
      contentType: "application/json",
      cacheControl: "no-cache",
    });
  }

  return filtered;
}

/* ---------------------------------- */
/* Helpers                             */
/* ---------------------------------- */

async function tryReadLayerIdsFromLayersConfig(
  storage: FirebaseStorage,
  layersConfigPath: string
): Promise<string[]> {
  // NOTE: With only Storage access, easiest is to fetch via the download URL.
  // If your environment already has fetch() available (web), this works.
  // If you're in Node, ensure you have fetch polyfilled (Node 18+ has it).
  try {
    const cfgRef = ref(storage, layersConfigPath);
    // Using the SDK to get a URL avoids needing Storage Rules bypass.
    const { getDownloadURL } = await import("firebase/storage");
    const url = await getDownloadURL(cfgRef);

    const res = await fetch(url);
    if (!res.ok) return [];
    const json = (await res.json()) as LayersConfig;

    return extractLayerIds(json);
  } catch {
    return [];
  }
}

/**
 * Your layers_config.json (uploaded) is an array of layer objects with various shapes. :contentReference[oaicite:6]{index=6}
 * This extractor is defensive:
 * - If it's an array, it looks for known id-ish fields.
 * - Otherwise returns [].
 */
function extractLayerIds(cfg: LayersConfig): string[] {
  if (!Array.isArray(cfg)) return [];

  const ids: string[] = [];

  for (const layer of cfg) {
    if (layer && typeof layer === "object") {
      const obj = layer as Record<string, unknown>;

      // Try a few common candidates
      const candidates = [
        obj["layerId"],
        obj["id"],
        obj["key"],
        obj["firebaseId"],
        obj["bazaartGuid"], // present in your sample config :contentReference[oaicite:7]{index=7}
      ];

      for (const c of candidates) {
        if (typeof c === "string" && c.length > 0) {
          ids.push(c);
          break;
        }
      }
    }
  }

  // Unique, preserve order
  return Array.from(new Set(ids));
}

/**
 * Recursively list all files under a Storage prefix.
 * listAll() is one-level, so we recurse into prefixes.
 */
async function listFilesRecursive(prefixRef: StorageReference): Promise<StorageReference[]> {
  const out: StorageReference[] = [];
  const stack: StorageReference[] = [prefixRef];

  while (stack.length > 0) {
    const current = stack.pop()!;
    let res;
    try {
      res = await listAll(current);
    } catch {
      // Folder might not exist (layer removed) or permissions.
      continue;
    }

    out.push(...res.items);
    // Recurse into "folders"
    for (const p of res.prefixes) stack.push(p);
  }

  return out;
}

function dedupeByFullPath(refs: StorageReference[]): StorageReference[] {
  const seen = new Set<string>();
  const out: StorageReference[] = [];
  for (const r of refs) {
    const k = r.fullPath;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(r);
  }
  return out;
}

/**
 * Concurrency-limited mapper (so we don't spam getMetadata()).
 */
async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length) as R[];
  let nextIndex = 0;

  async function worker() {
    while (true) {
      const i = nextIndex++;
      if (i >= items.length) return;
      results[i] = await fn(items[i]);
    }
  }

  const workers = Array.from({ length: Math.max(1, concurrency) }, () => worker());
  await Promise.all(workers);
  return results;
}

/**
 * Make a stable 12-digit-ish positive number from a string (path).
 * Uses FNV-1a 32-bit, then expands to a larger numeric space.
 */
function stableNumericId(input: string): number {
  const h = fnv1a32(input);
  // Expand to a 12-digit positive integer range-ish
  // (keeps within JS safe integer; 12 digits < 2^53)
  return 100_000_000_000 + (h % 900_000_000_000);
}

function fnv1a32(str: string): number {
  let h = 0x811c9dc5; // offset basis
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    // h *= 16777619 (but using bit ops for 32-bit overflow behavior)
    h = (h + (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24)) >>> 0;
  }
  return h >>> 0;
}